import { Injectable } from '@angular/core';
import {AuthedApiService} from '../authed-api.service';
import {Permission} from '../../enums/permission.enum';
import {PlatformAesService} from './platform-aes.service';
import {KongService} from '../kong.service';

@Injectable({
  providedIn: 'root'
})
export class AccountInfoService {

  private _isLoggedIn = false;
  private accountDetails: any;

  private _platformPermissions: any;
  private _accountPermissions: any;

  private _partnerId: string; // id in partner record (not required - has to be removed once all dependencies are closed)
  private _partner: string; // partner name
  private _account: string; // account name
  private _email: string; // account name
  private _partnerKey: string; // partner key (pkey)
  private _currentPlan: string;
  private _currentPlanDisplayName: string;
  private _kongApiKey;
  private _kingerHash: string;

  private permissionsReceived = false;
  private permissionsLoading = true;

  private profileReceived = false;
  private profileLoading = true;

  constructor(private apiService: AuthedApiService,
              private kong: KongService,
              private encryptionDecryptionService: PlatformAesService) { }

  get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
  }

  get partnerId(): string {
    return this._partnerId;
  }

  get partner(): string {
    return this._partner;
  }

  get account(): string {
    return this._account;
  }

  get email(): string {
    return this._email;
  }

  get partnerKey(): string {
    return this._partnerKey;
  }

  get currentPlan(): string {
    return this._currentPlan;
  }

  get currentPlanDisplayName(): string {
    return this._currentPlanDisplayName;
  }

  get kingerHash(): string {
    return this._kingerHash;
  }

  get kongApiKey(): string {
    return this._kongApiKey;
  }

  get partnerShortCode(): string {
    const temp = this.partner.split('-');
    return temp[temp.length - 1];
  }

  validateLogin(email: string, env: string, password: string) {
    return new Promise((resolve, reject) => {
      if (email && env) {
        if (password) {
          const encrypted = this.encryptionDecryptionService.encrypt(password);
          const loginPayload = {
            email: email,
            environment: env,
            password: btoa(encrypted.cipheredText + ':' + encrypted.salt + ':' + encrypted.iv)
          };
          this.apiService.validateLogin(loginPayload).subscribe(resp => {
            let response: any;
            response = resp;
            resolve(response);
          }, error => {
            if (error.error.status === 401) {
              reject('Incorrect password');
            } else {
              reject('Unknown error. Please try again.');
            }
          });
        } else {
          reject('Please enter a valid password');
        }
      } else {
        reject('Invalid email or environment name');
      }
    });
  }

  setAccountPermission() {
    return new Promise((resolve, reject) => {
      this.permissionsLoading = true;
      this.apiService.getUserAccountPermissions().subscribe(async data => {
        let permissionObj: any;
        permissionObj = data;
        this._accountPermissions = permissionObj.account ? permissionObj.account : [];
        this._platformPermissions = permissionObj.platform ? permissionObj.platform : [];
        this.permissionsReceived = true;
        this.permissionsLoading = false;
        resolve(true);
      }, error => {
        this.permissionsLoading = false;
        if (error && error.error && error.error.status === 401) {
          reject('Invalid or expired token');
        } else {
          let retries = -1;
          const retry = () => {
            retries++;
            if (retries >= 2) {
              reject('Unexpected error. Please login again.');
            } else {
              this.apiService.getUserAccountPermissions().subscribe(data => {
                let permissionObj: any;
                permissionObj = data;
                this._accountPermissions = permissionObj.account ? permissionObj.account : [];
                this._platformPermissions = permissionObj.platform ? permissionObj.platform : [];
                this.permissionsReceived = true;
                this.permissionsLoading = false;
                resolve(true);
              }, error1 => retry());
            }
          };
          retry();
        }
      });
    });
  }

  setAccountProfile() {
    return new Promise((resolve, reject) => {
      this.permissionsLoading = true;
      this.apiService.getAccountUserProfile().subscribe(async data => {
        let userObj: any;
        userObj = data;
        this.setAccountDetails(userObj);
        resolve(true);
      }, error => {
        this.permissionsLoading = false;
        if (error && error.error && error.error.status === 401) {
          reject('Invalid or expired token. Please login again.');
        } else {
          let retries = -1;
          const retry = () => {
            retries++;
            if (retries >= 2) {
              reject('Unexpected error. Please login again.');
            } else {
              this.apiService.getAccountUserProfile().subscribe(data => {
                let userObj: any;
                userObj = data;
                this.setAccountDetails(userObj);
                resolve(true);
              }, error1 => retry());
            }
          };
          retry();
        }
      });
    });
  }

  setAccountDetails(user: any) {
    if (user) {
      this.accountDetails = user;
      this._partner = user.partner;
      this._account = user.account;
      this._email = user.email;
      this._partnerKey = user.partnerKey;
      this._currentPlan = user.currentPlan;
      this._currentPlanDisplayName = user.currentPlanDisplayName;
      this._kingerHash = user.kinger_hash;
      this._partnerId = user._id;

      this.profileLoading = false;
      this.profileReceived = true;

      // These variables should not be stored in localstorage.
      // Has to be removed after all dependencies are changed to the assigned variables above
      localStorage.setItem('Auth', 'true');
      localStorage.setItem('partnerId', this._partnerId);
      localStorage.setItem('signInEmail', this._email);
      localStorage.setItem('partner', this._partner);
      localStorage.setItem('partnerKey', this._partnerKey);
      localStorage.setItem('currentPlan', this._currentPlan);
      localStorage.setItem('currentPlanDisplayName', this._currentPlanDisplayName);
      localStorage.setItem('account', this._account);
    }
  }

  hasPermissionsLoaded(): boolean {
    if (this.permissionsReceived) {
      return true;
    }
    return false;
  }

  hasPermission(permissionStr) {
    return this.hasPlatformPermission(permissionStr) || this.hasAccountPermission(permissionStr);
  }

  hasPlatformPermission(permissionStr) {
    return this._platformPermissions && this._platformPermissions.includes(Permission[permissionStr]);
  }

  hasPlatformPermissions() {
    return this._platformPermissions && this._platformPermissions.length > 0;
  }

  hasAccountPermission(permissionStr) {
    return this._accountPermissions && this._accountPermissions.includes(permissionStr);
  }

  hasPermissions(permissions) {
    if (!this._accountPermissions && !this._platformPermissions) {
      return false;
    }
    return permissions.every(permission =>
        this.hasAccountPermission(permission) || this.hasPlatformPermission(permission)
    );
  }

  getKongApiKeyForUser(signInEmail = this.email) {
    return new Promise((resolve, reject) => {
      this.kong.getKeyAuthForConsumerInApiGatewayByNameOrId(signInEmail).subscribe(keys => {
        let apiKeyData: any;
        apiKeyData = keys;
        if (apiKeyData.data !== null && apiKeyData.data !== undefined && apiKeyData.data.length !== 0) {
          const apiKeys = apiKeyData.data;
          const apiKey = apiKeys[0].key;
          localStorage.setItem('apikey', apiKey);
          this._kongApiKey = apiKey;
        }
        resolve('success');
      }, error => {
        resolve('failed');
      });
    });
  }
}
